<template>
  <div class="rooms">
    <div class="table-wrapper">
      <div v-if="errored" class="empty px-8 pt-6">
        <p>
          We are not able to retrieve this information at the moment, please try
          back later
        </p>
      </div>

      <div id="content-area" v-else>
        <div
          style="width: 100%; text-align: center"
          class="spin-holder mt-4"
          v-if="loading"
        >
          <i class="spinner fa fa-spinner fa-spin"></i>
          <p>Loading Rooms</p>
        </div>

        <div v-else>
          <div class="mb-12">
            <div class="grid grid-cols-2 space-x-3 py-2">
              <div class="w-11/12 rounded-lg bg-gray-100 p-4 ml-6 border">
                <h1 class="mb-4 font-bold text-base">School Rooms</h1>
                <p class="opacity-50" v-if="noSchoolRoomsSet">
                  You currently have no rooms setup.
                </p>

                <ul class="mb-1 w-full">
                  <li
                    v-for="item in schoolRooms"
                    :key="item.identifier"
                    class="inline-block mr-3 mb-2 text-gray-500"
                  >
                    <button
                      type="button"
                      @click="selectSchoolRoom(item)"
                      class="filter-item-button border-gray-300 border hover:bg-opacity-50 wide"
                      v-bind:class="
                        inSchoolRooms(item.identifier)
                          ? 'filter-item-button-on'
                          : 'filter-item-button-off'
                      "
                    >
                      <i
                        class="far pr-1 text-base text-gray-30 hover:text-action-blue"
                        v-bind:class="
                          inSchoolRooms(item.identifier)
                            ? 'fa-check-square'
                            : 'fa-square'
                        "
                      ></i>
                      {{ item.title }}
                    </button>
                  </li>
                </ul>
              </div>

              <div class="w-11/12 rounded-lg bg-gray-100 p-4 ml-6 border">
                <div class="flex justify-between">
                  <h1 class="font-bold text-base">Add New</h1>
                </div>

                <p class="opacity-50">
                  Add a new room to your school from the most used list or input
                  your own room.
                </p>
                <input
                  type="search"
                  class="filter-bar-search focus:outline-none mt-4"
                  name="search"
                  v-model="search"
                  placeholder="Search for rooms..."
                />

                <ul v-if="hasResults" class="mb-1 mt-4 w-full">
                  <li
                    v-for="item in filteredList"
                    :key="item.identifier"
                    class="inline-block mr-3 mb-2"
                  >
                    <button
                      type="button"
                      @click="selectSchoolRoom(item)"
                      class="filter-item-button text-gray-600 hover:text-action-blue border-gray-300 border hover:bg-opacity-50 wide"
                      v-bind:class="
                        inSchoolRooms(item.identifier)
                          ? 'filter-item-button-on'
                          : 'filter-item-button-off'
                      "
                    >
                      <i
                        class="far pr-1 text-base text-gray-30 hover:text-action-blue"
                        v-bind:class="
                          inSchoolRooms(item.identifier)
                            ? 'fa-check-square'
                            : 'fa-square'
                        "
                      ></i>
                      {{ item.title }}
                    </button>
                  </li>
                </ul>
                <p class="my-5 ml-2 text-red-400" v-else>
                  We could not find any data for your given selection.
                </p>

                <div class="grid grid-cols-1 space-y-0 pl-2">
                  <label
                    class="text-xs font-semibold text-gray-600 text-left tracking-wide"
                  >
                    Add Other</label
                  >

                  <div class="flex space-x-3 mr-4">
                    <div class="mb-5">
                      <input
                        v-model="newRoomValue"
                        class="p-3 w-64 bg-white border border-gray-200 rounded h-36 focus:bg-white focus:outline-none"
                        type="text"
                        placeholder="Enter room name"
                      />
                      <ul
                        v-if="showNewValueSuggestions"
                        class="absolute w-64 mt-1 rounded border-b border-l border-r bg-white focus:border-blue-400 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-40"
                      >
                        <li
                          v-for="item in suggestionList"
                          :key="item.id"
                          @click="selectSuggestion(item)"
                          class="cursor-pointer border-gray-300 p-2 px-4 text-gray-700 hover:bg-action-blue hover:bg-opacity-75 hover:text-white"
                        >
                          {{ item.title }}
                        </li>
                      </ul>
                    </div>

                    <button
                      @click="addNewRoomValue()"
                      type="submit"
                      class="publish-button h-10 mt-3 hover:opacity-50 w-24 justify-center tracking-wide focus:outline-none focus:shadow-outline shadow-lg cursor-pointer transition ease-in duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      :disabled="isDisabled"
                    >
                      Upload
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { roomMixin } from "@/mixins/roomMixin.js";

const FilterBar = () => import("@/components/FilterBarNavigation.vue");

export default {
  name: "rooms",
  mixins: [roomMixin],
  props: {},
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    hasResults() {
      return this.filteredList.length > 0;
    },
    filteredList() {
      let vm = this;
      var results = vm.rooms.filter(function (r) {
        var filtered = true;
        filtered = vm.inSchoolRooms(r.identifier) === false;

        if (filtered) {
          filtered = r.title.toLowerCase().includes(vm.search.toLowerCase());
        }
        return filtered;
      });

      const list = results.sort(
        (a, b) => b.used_count - a.used_count || a.title.localeCompare(b.title)
      );

      return list.slice(0, 12);
    },
    suggestionList() {
      let vm = this;
      var results = vm.rooms.filter(function (r) {
        var filtered = true;
        filtered = vm.inSchoolRooms(r.identifier) === false;

        if (filtered) {
          filtered = r.title
            .toLowerCase()
            .includes(vm.newRoomValue.toLowerCase());
        }
        return filtered;
      });

      const list = results.sort(
        (a, b) => b.used_count - a.used_count || a.title.localeCompare(b.title)
      );

      return list.slice(0, 12);
    },
    isDisabled() {
      return this.newRoomValue.length === 0;
    },
    showNewValueSuggestions() {
      return this.newRoomValue.length > 0 && this.filteredList.length > 0;
    },
  },
  data() {
    return {
      search: "",
      newRoomValue: "",
    };
  },
  created() {},
  mounted() {},
  methods: {
    setSearchValue(value, time = 500) {
      this.search = value;
    },
    selectSuggestion(room) {
      this.selectSchoolRoom(room);
      this.newRoomValue = "";
    },
    addNewRoomValue() {
      let payload = { title: this.newRoomValue };
      this.selectSchoolRoom(payload);
      this.newRoomValue = "";
    },
  },
  components: {
    FilterBar,
  },
};
</script>

<style>
i.icon {
  font-size: 18px;
  padding-left: 4px;
  padding-top: 4px;
}
</style>
